import {
  FormControlLabel,
  Switch as MuiSwitch,
  Typography,
} from '@mui/material';
import type { InputHTMLAttributes } from 'react';

import type { LabelPlacement } from './Switch.constants';
import { LABEL_PLACEMENT } from './Switch.constants';
import useStyles from './Switch.styles';

export type SwitchProps = {
  /** Text label displayed alongside the switch */
  label?: string;
  /** Whether the toggle is on or off */
  checked: boolean;
  /** Called when the toggle value changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Position of the label relative to the switch */
  labelPlacement?: LabelPlacement;
  /** Optional test id */
  dataTestId?: string;
  /** Accessible label for the input when no visible label is provided */
  ariaLabel?: string;
};

/**
 * Toggle switches are digital on/off switches. They prompt users to choose between two
 * mutually exclusive options and always have a default value. Toggles should provide
 * immediate results, giving users the freedom to control their preferences as needed.
 *
 * @see Design: https://www.figma.com/design/tHuavQeTzVIG2XBlMfBhrh/Commercial---Design-System-1.5?m=dev&node-id=503-22766
 */
const Switch = ({
  label,
  checked,
  onChange,
  disabled = false,
  labelPlacement = LABEL_PLACEMENT.END,
  dataTestId,
  ariaLabel,
}: SwitchProps) => {
  const { classes } = useStyles();

  const labelText = label && (
    <Typography className={classes.label} variant="body_medium">
      {label}
    </Typography>
  );

  return (
    <FormControlLabel
      className={classes.root}
      control={
        <MuiSwitch
          checked={checked}
          color="default"
          disabled={disabled}
          slotProps={{
            input: {
              ...(dataTestId && { 'data-testid': dataTestId }),
              ...(!label && ariaLabel && { 'aria-label': ariaLabel }),
            } as InputHTMLAttributes<HTMLInputElement>,
          }}
          onChange={(_, nextChecked) => onChange(nextChecked)}
        />
      }
      label={labelText}
      labelPlacement={labelPlacement}
      disabled={disabled}
    />
  );
};

export default Switch;
